import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Divider,
  Grid,
  Icon,
  Link,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
  AppBar,
  Button,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LoginIcon from '@mui/icons-material/Login';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupsIcon from '@mui/icons-material/Groups';
import { PricingCard } from './components/PricingCard';
import { PlanData } from '../../shared/constants/plans';
import { SconIcon } from '../../shared/assets';
import { publicService } from '../../shared/services/api/publicService';
import { FeedbackButton } from '../../shared/components/FeedbackButton/FeedbackButton';
import { PLANS_DATA } from '../../shared/constants/plans';

export const Landing: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  useEffect(() => {
    publicService
      .getWhatsAppContact()
      .then((contact) => setWhatsappNumber(contact.phoneNumber))
      .catch(() => setWhatsappNumber('+5551993848249')); // fallback — mesmo do appsettings.Development.json
  }, []);

  const handleContractPlan = (plan: { name: string; slug: string }) => {
    const cycle = billingCycle === 'annual' ? 'yearly' : 'monthly';
    navigate(`/create-business-trial?plan=${plan.slug}&cycle=${cycle}`);
  };

  const formatPrice = (value: number): string =>
    `R$ ${value.toFixed(2).replace('.', ',')}`;

  const plans = PLANS_DATA;

  const yearlyDisplay = (plan: PlanData): string | null =>
    billingCycle === 'annual'
      ? `ou R$ ${plan.priceYearly.toFixed(2).replace('.', ',')}/ano`
      : null;

  const annualSavings = (plan: PlanData): string | null => {
    if (billingCycle !== 'annual') return null;
    const monthlyTotal = plan.priceMonthly * 12;
    const savings = monthlyTotal - plan.priceYearly;
    if (savings <= 0) return null;
    return `Economia de R$ ${savings.toFixed(0).replace('.', ',')}/ano`;
  };

  const featuresData = [
    {
      icon: <SmartToyIcon sx={{ fontSize: 40 }} />,
      title: 'IA no WhatsApp',
      desc: 'Sua barbearia com inteligência artificial para agendar, responder e automatizar atendimentos 24h.',
    },
    {
      icon: <CalendarMonthIcon sx={{ fontSize: 40 }} />,
      title: 'Agendamento Online',
      desc: 'Clientes marcam horários pelo link sem precisar ligar. Reduza faltas com lembretes automáticos.',
    },
    {
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />,
      title: 'Gestão Financeira',
      desc: 'Controle entradas, saídas, comissões e fluxo de caixa em tempo real. Saiba exatamente quanto você ganha.',
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 40 }} />,
      title: 'Gestão de Equipe',
      desc: 'Controle horários, comissões e permissões de cada profissional. Tudo organizado em um só lugar.',
    },
  ];

  const handleBillingChange = (_: React.MouseEvent<HTMLElement>, newCycle: 'monthly' | 'annual') => {
    if (newCycle) setBillingCycle(newCycle);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default, overflowX: 'hidden' }}>
      {/* Top Bar */}
      <AppBar position="absolute" color="transparent" elevation={0} sx={{ background: 'transparent' }}>
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={SconIcon}
                alt="SconApp"
                sx={{ width: 36, height: 36 }}
              />
              <Typography variant="h6" fontWeight="bold" color="white">
                SconApp
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/home')}
              sx={{
                backgroundColor: '#ffffff',
                color: theme.palette.secondary.main,
                fontWeight: 'bold',
                borderRadius: 6,
                px: 3,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Área Logada
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          pt: { xs: 16, sm: 12 },
          pb: 12,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                fontWeight={800}
                gutterBottom
                sx={{
                  textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                Gestão + Automação + Presença Digital para seu negócio de beleza
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                }}
              >
                Agendamento inteligente, controle financeiro e IA no WhatsApp.
                Tudo que barbearias e salões precisam para crescer.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: '#ffffff',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    borderRadius: 8,
                    '&:hover': {
                      backgroundColor: theme.palette.secondary.dark,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => {
                    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Comece Grátis
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    color: '#ffffff',
                    borderColor: 'rgba(255,255,255,0.5)',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    borderRadius: 8,
                    '&:hover': {
                      borderColor: '#ffffff',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                  onClick={() => {
                    document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Saiba Mais
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                component="img"
                src="/app-interface-mockup.png"
                alt="App SconApp"
                sx={{
                  width: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                  animation: 'float 6s ease-in-out infinite',
                  '@keyframes float': {
                    '0%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-20px)' },
                    '100%': { transform: 'translateY(0px)' },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features-section">
        <Container maxWidth="lg" sx={{ py: 10 }}>
          <Typography variant="h3" textAlign="center" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            Por que escolher o SconApp?
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: 'auto' }}
          >
            Uma plataforma completa que vai muito além de uma simples agenda.
          </Typography>
          <Grid container spacing={4}>
            {featuresData.map((feature, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    textAlign: 'center',
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: theme.shadows[4],
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" textAlign="center" fontWeight="bold" gutterBottom>
            Como funciona
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: 'auto' }}
          >
            Comece a usar o SconApp em apenas 3 passos simples.
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {[
              {
                step: '1',
                icon: 'how_to_reg',
                title: 'Faça seu Cadastro',
                desc: 'Crie sua conta gratuitamente. Sem cartão de crédito — apenas 30 dias grátis para testar tudo.',
              },
              {
                step: '2',
                icon: 'tune',
                title: 'Configure seu Negócio',
                desc: 'Adicione seus serviços, profissionais, horários e personalize sua página pública.',
              },
              {
                step: '3',
                icon: 'rocket_launch',
                title: 'Comece a Atender',
                desc: 'Clientes agendam online, você gerencia tudo em um só lugar e sua equipe trabalha integrada.',
              },
            ].map((item) => (
              <Grid item xs={12} md={4} key={item.step}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ fontSize: 32 }}>{item.icon}</Icon>
                  </Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {item.step}. {item.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {item.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box
        id="pricing-section"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#f8fafc',
          py: 10,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" textAlign="center" fontWeight="bold" gutterBottom>
            Planos sob medida
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
            Cresça com o plano ideal para seu negócio
          </Typography>

          {/* Billing Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              onChange={handleBillingChange}
              sx={{
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)',
                borderRadius: 3,
                p: 0.5,
                '& .MuiToggleButton-root': {
                  px: 3,
                  py: 1,
                  borderRadius: 2.5,
                  border: 'none',
                  textTransform: 'none',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="monthly">Mensal</ToggleButton>
              <ToggleButton value="annual">
                Anual
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    fontSize: '0.7rem',
                    backgroundColor: theme.palette.success.main,
                    color: '#ffffff',
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 1,
                    fontWeight: 700,
                  }}
                >
                  ECONOMIZE
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Typography
            variant="body2"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 4, mt: -4 }}
          >
            {billingCycle === 'monthly'
              ? '💳 Assinatura mensal recorrente (cartão de crédito)'
              : '💰 Pagamento único via Pix ou cartão (à vista ou parcelado)'}
          </Typography>

          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {plans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.slug}>
                <PricingCard
                  plan={plan}
                  billingCycle={billingCycle}
                  formatPrice={formatPrice}
                  yearlyDisplay={yearlyDisplay}
                  annualSavings={annualSavings}
                  onContract={() =>
                    handleContractPlan({
                      name: plan.name,
                      slug: plan.slug,
                    })
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ py: 10, textAlign: 'center', backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#f8fafc' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Pronto para transformar seu negócio?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Comece hoje e descubra como o SconApp pode revolucionar a gestão
            da sua barbearia ou salão.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
            sx={{
              px: 6,
              py: 2,
              borderRadius: 8,
              fontWeight: 'bold',
              fontSize: '1.1rem',
            }}
          >
            Comece Agora
          </Button>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Typography variant="h3" textAlign="center" fontWeight="bold" gutterBottom>
            Perguntas Frequentes
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Tire suas dúvidas sobre o SconApp
          </Typography>
          {[
            {
              question: 'Preciso de cartão de crédito para testar?',
              answer: 'Não! Oferecemos 30 dias de teste gratuito sem necessidade de cartão de crédito. Você pode explorar todas as funcionalidades sem compromisso.',
            },
            {
              question: 'Posso usar o SconApp sem ter CNPJ?',
              answer: 'Sim! O SconApp pode ser utilizado tanto por Pessoa Jurídica quanto por Pessoa Física. Não exigimos CNPJ para cadastro.',
            },
            {
              question: 'Meus profissionais também têm acesso ao sistema?',
              answer: 'Sim! Cada profissional pode ter seu próprio acesso com permissões personalizadas. Eles podem ver sua agenda, comissões e carteira, enquanto você controla o que cada um visualiza.',
            },
            {
              question: 'Consigo mudar de plano depois?',
              answer: 'Sim! Você pode fazer upgrade ou downgrade do seu plano a qualquer momento. A diferença é ajustada proporcionalmente na próxima fatura.',
            },
            {
              question: 'O SconApp envia lembretes automáticos para os clientes?',
              answer: 'Sim! O sistema envia lembretes automáticos de agendamentos via WhatsApp, reduzindo faltas em até 80%.',
            },
          ].map((faq, idx) => (
            <Accordion key={idx} sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' } }} elevation={0}>
              <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                <Typography fontWeight={600}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#1e293b',
          color: '#fff',
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box component="img" src={SconIcon} alt="SconApp" sx={{ width: 32, height: 32 }} />
                <Typography variant="h6" fontWeight="bold">
                  SconApp
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
                Plataforma completa de gestão para barbearias, salões e clínicas de estética.
                Agenda online, IA no WhatsApp, controle financeiro e muito mais.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Segmentos
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Link href="#" color="inherit" underline="none" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                  Barbearias
                </Link>
                <Link href="#" color="inherit" underline="none" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                  Salões de Beleza
                </Link>
                <Link href="#" color="inherit" underline="none" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                  Clínicas de Estética
                </Link>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Link href="#" color="inherit" underline="none" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                  Planos
                </Link>
                <Link href="#" color="inherit" underline="none" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                  Sobre
                </Link>
                <Link href="#" color="inherit" underline="none" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                  Contato
                </Link>
                <Link href="#" color="inherit" underline="none" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                  Central de Ajuda
                </Link>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Legal
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Link href="#" color="inherit" underline="none" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                  Política de Privacidade
                </Link>
                <Link href="#" color="inherit" underline="none" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                  Termos de Uso
                </Link>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Contato
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {whatsappNumber && (
                  <Link
                    href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="none"
                    sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                  >
                    WhatsApp
                  </Link>
                )}
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  contato@sconapp.com.br
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.5 }}>
            © {new Date().getFullYear()} SconApp. Todos os direitos reservados.
          </Typography>
        </Container>
      </Box>

      <FeedbackButton />
    </Box>
  );
};
